/**
 * Shared per-layer icons, keyed by layer id, used by BOTH the 2D layers
 * dropdown and the 3D layers panel (via LayerToggleRow's `icon` prop) so the
 * two menus show identical glyphs and can't drift. See 3D-layers plan Phase C.
 */

import { ReactNode } from 'react';

export const LAYER_ICONS: Record<string, ReactNode> = {
  fire_boundaries: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C13 5 15 7 15 10C15 12 14 13 12 14C14 13 16 12 16 9C16 7 15 5 12 2ZM8 14C8 11 10 9 12 7C10 9 9 11 9 14C9 16 10 18 12 18C10 18 8 16 8 14ZM12 22C8 22 5 19 5 15C5 12 7 9 10 7C8 10 7 12 7 15C7 18 9 20 12 20C15 20 17 18 17 15C17 12 16 10 14 7C17 9 19 12 19 15C19 19 16 22 12 22Z"/>
    </svg>
  ),
  warning_areas: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3,2">
      <polygon points="12,2 22,20 2,20" />
    </svg>
  ),
  hotspots: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ color: '#f97316' }}>
      <circle cx="12" cy="12" r="4"/>
    </svg>
  ),
  planned_burns: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3,2" style={{ color: '#9ca3af' }}>
      <path d="M12 3 L22 21 L2 21 Z" />
    </svg>
  ),
  road_closures: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
      <line x1="12" y1="8" x2="12" y2="16"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  power_outages: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ color: '#eab308' }}>
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
    </svg>
  ),
  aircraft: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>
  ),
  photos: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  ),
  wind: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/>
    </svg>
  ),
  smoke: (
    <svg viewBox="0 0 24 24" fill="currentColor" opacity="0.7" style={{ color: '#9ca3af' }}>
      <circle cx="8" cy="10" r="4"/>
      <circle cx="14" cy="8" r="3"/>
      <circle cx="12" cy="14" r="3"/>
    </svg>
  ),
  air_quality: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
  ),
  city_temperatures: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z"/>
    </svg>
  ),
  thunderstorm_warnings: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ color: '#eab308' }}>
      <path d="M17 10H20L13 21V13H9L11 6H7V3H17V10Z"/>
    </svg>
  ),
  dry_lightning_risk: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ color: '#f97316' }}>
      <path d="M11 21h-1l1-7H7.5c-.88 0-.33-.75-.31-.78C8.48 10.94 10.42 7.54 13.01 3h1l-1 7h3.51c.4 0 .62.19.4.66C12.97 17.55 11 21 11 21z"/>
      <circle cx="18" cy="5" r="3" fill="#dc2626" opacity="0.8"/>
    </svg>
  ),
  // Supporter / premium imagery layers
  high_res: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fbbf24' }}>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
      <path d="M11 8v6M8 11h6" />
    </svg>
  ),
  satellite_imagery: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#60a5fa' }}>
      <path d="M4 12a8 8 0 0 1 8-8" />
      <path d="M4 16a4 4 0 0 1 4 4" />
      <circle cx="5" cy="19" r="1.5" fill="currentColor" stroke="none" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" transform="rotate(45 17 7)" />
    </svg>
  ),
  // 3D-only layers
  ember_attack: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ color: '#f97316' }}>
      <circle cx="6" cy="7" r="1.6"/>
      <circle cx="17" cy="6" r="1.2"/>
      <circle cx="19" cy="13" r="1.6"/>
      <circle cx="9" cy="15" r="1.2"/>
      <circle cx="14" cy="18" r="1.4"/>
      <circle cx="4" cy="16" r="1"/>
    </svg>
  ),
  fire_spread: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="12" x2="12" y2="4"/><polyline points="9 7 12 4 15 7"/>
      <line x1="12" y1="12" x2="19" y2="16"/><polyline points="19 12 19 16 15 16"/>
      <line x1="12" y1="12" x2="5" y2="16"/><polyline points="9 16 5 16 5 12"/>
    </svg>
  ),
  town_labels: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
};
